import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import type {WorksModel} from "src/app/models/WorksModel";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {PublicWorkEntryService} from "../../services/public-work-entry.service";
import {PublicShareDetailsComponent} from "../public-share-details/public-share-details.component";
import {WorkEchoButtonComponent} from "../work-echo-button/work-echo-button.component";
import {PublicWorkService} from "../../services/public-work.service";
import {ToastService} from "src/app/modules/core/services/toast.service";
import type {PublicWorkContext} from "../../interfaces/public-work.interface";
import {combineLatest, of, switchMap} from "rxjs";

@Component({
  selector: "app-public-work-context",
  templateUrl: "./public-work-context.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    PublicShareDetailsComponent,
    WorkEchoButtonComponent,
  ],
})
export class PublicWorkContextComponent implements OnInit, OnChanges {
  @Input({required: true}) item!: WorksModel;
  @ViewChild(WorkEchoButtonComponent) echo?: WorkEchoButtonComponent;
  saving = false;
  error = false;
  private context?: PublicWorkContext;
  constructor(
    readonly entry: PublicWorkEntryService,
    private readonly auth: AuthService,
    private readonly works: WorksService,
    private readonly modal: ModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly publicWorks: PublicWorkService,
    private readonly toast: ToastService
  ) {
    this.destroyRef.onDestroy(() => this.clearContext());
    afterNextRender(() => {
      if (this.visible && this.entry.intent() === "list") this.openLists();
      if (
        this.visible &&
        this.entry.intent() === "echo" &&
        this.echo?.shouldShowEchoButton
      ) {
        this.consume();
        this.echo.startEcho(true);
      }
    });
  }

  ngOnChanges(): void {
    if (this.visible) this.context = this.entry.page();
  }

  ngOnInit(): void {
    combineLatest([this.modal.modals$, this.auth.user$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([modals, user]) => {
        if (!modals[this.item._id] || !this.entry.ownedBy(user?._id))
          this.clearContext();
      });
  }

  private clearContext(): void {
    if (this.context && this.entry.page() === this.context) this.entry.clear();
    this.context = undefined;
  }

  get visible(): boolean {
    return (
      this.entry.page()?.work.id === this.item._id &&
      this.entry.ownedBy(this.auth.user?._id)
    );
  }

  get attributedShare() {
    const share = this.entry.page()?.share;
    return share?.authorId === this.auth.user?._id ? null : share;
  }

  confirmRating(): void {
    const user = this.auth.user;
    const rating = this.entry.rating();
    if (!user || !this.visible || rating === undefined || this.saving) return;
    this.saving = true;
    this.error = false;
    const work = this.item.inUserGemster
      ? of(this.item)
      : this.works.addWorkByType({workId: this.item._id}, this.item.type, user);
    work
      .pipe(
        switchMap((item) => {
          // Keep the addition after a failed rating request so a retry only saves the rating.
          this.item = item;
          return this.works.updateWorkRatingByType(
            user._id,
            item._id,
            item.type,
            rating
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.works.setEditedItem({...this.item, userRating: rating});
          this.toast.sendToast({
            text: "RATING_UPDATED",
            classes: "bg-form-success",
          });
          this.consume();
          this.saving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = true;
          this.saving = false;
          this.cdr.markForCheck();
        },
      });
  }

  openLists(): void {
    this.consume();
    this.modal.openItemModal(`addList_${this.item.providerId}`, this.item);
  }

  consume(): void {
    this.entry.consume();
    this.publicWorks
      .clearResume()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          /* The server context also expires. */
        },
      });
  }
}
